// Client portal Returns tab — read-only view of return labels with the
// post-margin "charged" amount surfaced by the backend (carrier_cost is
// censored out, so all the client ever sees is what they're billed).

import { useMemo, useState } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { AlertTriangle, Loader2, FileText } from 'lucide-react'
import { getMeReturnLabels, openMeReturnLabelPdf } from '../../../lib/api'
import {
  formatCurrency, formatDate,
  currentMonthStart, todayStr, DateRangeFilter,
} from './helpers'

export default function ReturnsTab({ clientId }) {
  const [from, setFrom] = useState(currentMonthStart())
  const [to,   setTo]   = useState(todayStr())

  const params = useMemo(() => ({
    ...(from && { from }),
    ...(to   && { to   }),
  }), [from, to])

  const { data: labels = [], isLoading, isError, error } = useQuery({
    queryKey: ['me', 'returns', clientId, params],
    queryFn:  () => getMeReturnLabels(clientId, params),
    enabled:  !!clientId,
  })

  const total = labels.reduce((s, l) => s + (Number(l.charged) || 0), 0)
  const active = labels.filter(l => !l.voided)

  // PDF link only works for ShipStation-sourced labels; manual + Easyship
  // rows have no upstream PDF and show '—'.
  const pdfMutation = useMutation({
    mutationFn: (labelId) => openMeReturnLabelPdf(clientId, labelId),
    onError: (err) => alert(err?.response?.data?.detail || 'Failed to open label PDF.'),
  })

  return (
    <div className="space-y-4">
      <div className="card p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-slate-700">Return labels</h3>
            <p className="text-xs text-slate-400 mt-0.5">
              Each label shows the amount you're billed (post any flat-rate or carrier exception rules).
            </p>
          </div>
          <DateRangeFilter from={from} to={to} onChange={({ from: f, to: t }) => { setFrom(f); setTo(t) }} />
        </div>
      </div>

      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12 gap-2 text-slate-400">
            <Loader2 size={18} className="animate-spin" />
            <span className="text-sm">Loading…</span>
          </div>
        ) : isError ? (
          // Fetch failure — surface it so a broken endpoint doesn't look
          // like "no returns in this period" (which would be reassuring
          // and wrong).
          <div className="p-4 text-sm text-red-700 bg-red-50 border-t border-red-200 flex items-start gap-2">
            <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
            <span>Failed to load: {error?.response?.data?.detail || error?.message || 'Unknown error'}</span>
          </div>
        ) : labels.length === 0 ? (
          <div className="p-6 text-center text-sm text-slate-400">No return labels in this period.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  <th className="px-4 py-2.5">Date</th>
                  <th className="px-4 py-2.5">Order #</th>
                  <th className="px-4 py-2.5">Tracking</th>
                  <th className="px-4 py-2.5">Carrier</th>
                  <th className="px-4 py-2.5">Service</th>
                  <th className="px-4 py-2.5 text-right">Charged</th>
                  <th className="px-4 py-2.5">Status</th>
                  <th className="px-4 py-2.5 text-center">PDF</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {labels.map((l, i) => (
                  <tr key={l.id || i} className={`${i % 2 === 1 ? 'bg-slate-50/40' : ''} ${l.voided ? 'opacity-50' : ''}`}>
                    <td className="px-4 py-2 text-slate-600">{formatDate(l.label_created_at)}</td>
                    <td className="px-4 py-2 font-mono text-xs text-slate-700">{l.order_number || '—'}</td>
                    <td className="px-4 py-2 font-mono text-xs text-slate-500">{l.tracking_number || '—'}</td>
                    <td className="px-4 py-2 text-slate-600">{l.carrier_code || '—'}</td>
                    <td className="px-4 py-2 text-slate-600">{l.service_code || '—'}</td>
                    <td className={`px-4 py-2 text-right tabular-nums ${l.voided ? 'line-through' : 'text-slate-800 font-medium'}`}>
                      {formatCurrency(l.charged)}
                    </td>
                    <td className="px-4 py-2">
                      {l.voided
                        ? <span className="text-xs px-1.5 py-0.5 rounded bg-red-50 text-red-600">Voided</span>
                        : l.manual
                          ? <span className="text-xs px-1.5 py-0.5 rounded bg-violet-50 text-violet-700">Manual</span>
                          : l.source === 'easyship'
                            ? <span className="text-xs px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">Easyship</span>
                            : <span className="text-xs text-slate-400">—</span>}
                    </td>
                    <td className="px-4 py-2 text-center">
                      {l.source !== 'easyship' && !l.manual && l.tracking_number ? (
                        <button
                          onClick={() => pdfMutation.mutate(l.id)}
                          disabled={pdfMutation.isPending}
                          title="Open the return label PDF"
                          className="p-1 rounded transition-colors text-slate-300 hover:text-blue-600 hover:bg-blue-50 disabled:opacity-50"
                        >
                          {pdfMutation.isPending && pdfMutation.variables === l.id
                            ? <Loader2 size={14} className="animate-spin" />
                            : <FileText size={14} />}
                        </button>
                      ) : (
                        <span className="text-slate-300 text-xs">—</span>
                      )}
                    </td>
                  </tr>
                ))}
                <tr className="bg-slate-50 border-t-2 border-slate-200">
                  <td colSpan={5} className="px-4 py-2.5 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Total ({active.length} label{active.length !== 1 ? 's' : ''})
                  </td>
                  <td className="px-4 py-2.5 text-right font-bold text-slate-800 tabular-nums">{formatCurrency(total)}</td>
                  <td />
                  <td />
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
